<script setup lang="ts">
import BaseButton from '@/components/base/BaseButton.vue'
import BaseIcon from '@/components/base/BaseIcon.vue'
import type { NavItem } from '@/constants/app/navigation.constant.js'

import SectionNavItems from './SectionNavItems.vue'

/**
 * The 248 px section nav: the items of the active rail section that this operator may see.
 *
 * Filtering happens in `AppLayout`, not here — a component that both decides and draws is
 * a component whose decision cannot be tested without mounting it. The rows themselves are
 * `SectionNavItems`, shared with the rail's flyout so the collapsed and expanded shells
 * show the same thing.
 *
 * The collapse control lives here, on the column it collapses. Its counterpart for getting
 * back lives in the header, because a control that disappears with the thing it hides
 * cannot bring it back.
 */
defineProps<{ label: string; items: readonly NavItem[] }>()
const emit = defineEmits<{ collapse: [] }>()
</script>

<template>
  <nav
    class="hidden w-[var(--section-nav-width)] shrink-0 border-r border-border-subtle bg-raised px-3 py-4 lg:block"
    :aria-label="label"
  >
    <div class="mb-2 flex items-center justify-between gap-2 pl-2">
      <p class="text-micro text-ink-muted">{{ label }}</p>
      <BaseButton
        variant="ghost"
        aria-label="Hide the section menu"
        title="Hide the section menu"
        @click="emit('collapse')"
      >
        <BaseIcon
          name="sidebar-simple"
          :size="16"
        />
      </BaseButton>
    </div>

    <SectionNavItems :items="items" />
  </nav>
</template>
